import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePassportProfile, useCreatePassportProfile } from '@/hooks/useConnectApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { User, Building2, ArrowLeft, Loader2, Wrench, ClipboardList, Settings, GraduationCap } from 'lucide-react';
import { JoinSchoolFlow } from '@/components/onboarding/JoinSchoolFlow';

const ROLE_OPTIONS = [
  { value: 'technician', label: 'Technician', description: 'Hands-on repair and diagnostics work', icon: Wrench },
  { value: 'advisor', label: 'Service Advisor / Writer', description: 'Customer-facing service coordination', icon: ClipboardList },
  { value: 'manager', label: 'Manager', description: 'Team leadership and shop operations', icon: Settings },
] as const;

const Onboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, membership, userShops, isShopDataLoading, isLoading: isAuthLoading } = useAuth();
  const { data: passportProfile, isLoading: isPassportLoading } = usePassportProfile();
  const createPassport = useCreatePassportProfile();
  const [isCreating, setIsCreating] = useState(false);
  const [step, setStep] = useState<'choose' | 'passport-role' | 'join-school'>('choose');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [displayName, setDisplayName] = useState('');

  // Initialize display name from email
  useEffect(() => {
    if (user?.email && !displayName) {
      setDisplayName(user.email.split('@')[0] || '');
    }
  }, [user?.email, displayName]);

  // Detect invitation tokens — redirect to dedicated join flow
  useEffect(() => {
    if (!user) return;

    const searchParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(
      location.hash.includes('?') ? location.hash.substring(location.hash.indexOf('?') + 1) : ''
    );

    const token = searchParams.get('token') || hashParams.get('token');
    const invitation = searchParams.get('invitation') || hashParams.get('invitation');

    if (token) {
      navigate(`/join-team?token=${token}`, { replace: true });
    } else if (invitation) {
      navigate(`/join-team?token=${invitation}`, { replace: true });
    }
  }, [user, location.search, location.hash, navigate]);

  // Redirect based on hard facts (no isPassportMode)
  useEffect(() => {
    if (!user || isShopDataLoading || isPassportLoading) return;

    // User already has an active shop membership
    if (membership || (userShops?.length || 0) > 0) {
      navigate('/', { replace: true });
      return;
    }

    // User already has a passport profile
    if (passportProfile) {
      navigate('/passport', { replace: true });
      return;
    }
  }, [user, isShopDataLoading, isPassportLoading, membership, userShops, passportProfile, navigate]);

  const handleCreatePassport = () => {
    if (!user || isCreating || !selectedRole || !displayName.trim()) return;
    setIsCreating(true);

    createPassport.mutate(
      { display_name: displayName.trim(), roles: [selectedRole] },
      {
        onSuccess: () => navigate('/passport', { replace: true }),
        onSettled: () => setIsCreating(false),
      }
    );
  };

  const handleSetUpShop = () => {
    navigate('/onboard/shop');
  };

  // FIX-R128-HIGH-2: defensive redirect to /auth when auth has resolved
  // with no user. Previously this branch only checked `!user` and rendered
  // a permanent "Loading..." with no recovery — so any landing on /onboard
  // without a session (direct nav, expired session, post-signup-pending-
  // confirmation, session lost mid-flow) trapped the user on a screen
  // that pretended to be loading. We separate the two states:
  //   1. auth still resolving → "Loading..."
  //   2. auth resolved + no user → redirect to /auth (re-entry path).
  if (isAuthLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Loading state — auth is resolved, user exists, but shop/passport data still loading
  if (isShopDataLoading || isPassportLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  // If redirects above will fire, show nothing briefly
  if (membership || (userShops?.length || 0) > 0 || passportProfile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  // Step: Join a School
  if (step === 'join-school') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-lg space-y-6">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-muted-foreground"
            onClick={() => setStep('choose')}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <JoinSchoolFlow
            displayName={displayName}
            onComplete={() => navigate('/passport', { replace: true })}
          />
        </div>
      </div>
    );
  }

  // Step 2: Role selection for passport
  if (step === 'passport-role') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-lg space-y-6">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-muted-foreground"
            onClick={() => setStep('choose')}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>

          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">Set Up Your Passport</h1>
            <p className="text-muted-foreground">Tell us about yourself to get started.</p>
          </div>

          <Card>
            <CardContent className="pt-6 space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="display-name">Display Name</Label>
                <Input
                  id="display-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>

              <div className="space-y-3">
                <Label>What's your primary role?</Label>
                <RadioGroup value={selectedRole} onValueChange={setSelectedRole} className="space-y-2">
                  {ROLE_OPTIONS.map((role) => {
                    const Icon = role.icon;
                    return (
                      <label
                        key={role.value}
                        className={`flex items-center gap-3 rounded-lg border-2 p-4 cursor-pointer transition-colors ${
                          selectedRole === role.value
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/30'
                        }`}
                      >
                        <RadioGroupItem value={role.value} id={`role-${role.value}`} />
                        <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
                        <div>
                          <div className="font-medium text-sm">{role.label}</div>
                          <div className="text-xs text-muted-foreground">{role.description}</div>
                        </div>
                      </label>
                    );
                  })}
                </RadioGroup>
              </div>

              <Button
                className="w-full"
                disabled={isCreating || !selectedRole || !displayName.trim()}
                onClick={handleCreatePassport}
              >
                {isCreating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Passport
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Step 1: Choose path
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-2xl space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">How are you using OptiBay?</h1>
          <p className="text-muted-foreground">Choose how you'd like to get started.</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Card
            className="cursor-pointer transition-shadow hover:shadow-md border-2 hover:border-primary/50"
            onClick={() => setStep('passport-role')}
          >
            <CardHeader className="items-center text-center pb-2">
              <User className="h-10 w-10 text-primary mb-2" />
              <CardTitle className="text-lg">Start a Passport</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <CardDescription>
                For technicians, advisors, and managers building a personal profile.
              </CardDescription>
              <Button
                className="mt-4 w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  setStep('passport-role');
                }}
              >
                Start Passport
              </Button>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer transition-shadow hover:shadow-md border-2 hover:border-primary/50"
            onClick={handleSetUpShop}
          >
            <CardHeader className="items-center text-center pb-2">
              <Building2 className="h-10 w-10 text-primary mb-2" />
              <CardTitle className="text-lg">Set Up a Shop</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <CardDescription>
                For owners and teams setting up OptiBay for a facility.
              </CardDescription>
              <Button variant="outline" className="mt-4 w-full" onClick={(e) => {
                e.stopPropagation();
                handleSetUpShop();
              }}>
                Set Up Shop
              </Button>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer transition-shadow hover:shadow-md border-2 hover:border-primary/50"
            onClick={() => setStep('join-school')}
          >
            <CardHeader className="items-center text-center pb-2">
              <GraduationCap className="h-10 w-10 text-primary mb-2" />
              <CardTitle className="text-lg">Join a School</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <CardDescription>
                For students enrolling in a school program with a join code.
              </CardDescription>
              <Button variant="outline" className="mt-4 w-full" onClick={(e) => {
                e.stopPropagation();
                setStep('join-school');
              }}>
                Join School
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Onboard;
